import { Show, SignIn } from '@clerk/react'
import { SizableText, View } from 'tamagui'
// API providers removed - now using direct tRPC hooks with @tanstack/react-query
import { AiHelpProvider } from 'aiHelp/AiHelpProvider'
import AiHelpWidget from 'aiHelp/AiHelpWidget'
import { GeneralWebsocketProvider } from 'apiProviders/websocketProvider'
import TopBar from 'components/topbar'
import { useOrgId } from 'helpers'
import { useIsMobile } from 'helperFunctions/pageSize'
import OnboardingGuide from 'onboarding/OnboardingGuide'
import OnboardingHighlight from 'onboarding/OnboardingHighlight'
import { OnboardingProvider, useOnboarding } from 'onboarding/OnboardingContext'
import PublicWishlist from 'publicWishlist'
import Purchasers from 'purchasers'
import { omit } from 'ramda'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom'
import { captureRefFromUrl } from 'utils/referral'
import RolesPage from 'roles'
import {
    DocusignConnectionResponse,
    LoggedInDocusignConnectionResponse,
} from 'settings/docusignConnectionResponse'
import SettingsPage from 'settings/settingsPage'
import { LoggedInXeroConnectionResponse, XeroConnectionResponse } from 'settings/xeroConnectionResponse'
import ConjunctionalAgencies from 'users/conjunctionalAgencies'
import AdminApp from './admin'
import AffiliateApp from './affiliate'
import DocusignSenderReturn from './docusignSenderReturn'
import SignupWizard from './signup'
import { useOrgListData, UserDataContext, UserProvider } from './auth'
import UserSettings from './userSettings'

import AiHelpHighlight from 'aiHelp/AiHelpHighlight'

function AiHelpWidgetWithOnboarding() {
    const { expanded, isInTourStepFlow } = useOnboarding()
    const sidebarOpen = expanded && !isInTourStepFlow
    return <AiHelpWidget onboardingSidebarOpen={sidebarOpen} />
}

const OrgList = () => {
    const { orgListUserData } = useOrgListData()
    const navigate = useNavigate()

    return (
        <View padding="$8">
            <SizableText
                size="$6"
                marginBottom="$4"
            >
                Orgs
            </SizableText>
            {Object.entries(orgListUserData.orgData).map(([orgId, orgData]) => (
                <View
                    key={orgId}
                    borderWidth={1}
                    borderColor="$borderColor"
                    borderRadius="$2"
                    padding="$2"
                    cursor="pointer"
                    hoverStyle={{
                        backgroundColor: '$backgroundHover',
                    }}
                    onPress={() => navigate(`/${orgId}`)}
                >
                    {orgData.orgName}
                </View>
            ))}
        </View>
    )
}

const RedirectTo = ({ to }: { to: string }) => {
    const orgId = useOrgId()
    return (
        <Navigate
            to={`/${orgId}/${to}`}
            replace
        />
    )
}

const OrgRoutes = () => (
    <Routes>
        <Route
            path="/xero/auth"
            element={<LoggedInXeroConnectionResponse />}
        />
        <Route
            path="/docusign/auth"
            element={<LoggedInDocusignConnectionResponse />}
        />
        <Route
            path="/adduser"
            element={<RedirectTo to="settings/users/new" />}
        />
        <Route
            path="/users/*"
            element={<RedirectTo to="settings/users" />}
        />
        <Route
            path="/roles"
            element={<RolesPage />}
        />
        <Route
            path="/conjunctionalagencies/*"
            element={<ConjunctionalAgencies />}
        />
        <Route
            path="/clients/*"
            element={<Purchasers />}
        />
        <Route
            path="/settings/*"
            element={<SettingsPage />}
        />
        <Route
            path="/usersettings/*"
            element={<UserSettings />}
        />
        <Route
            index
            element={
                <Navigate
                    to="clients"
                    replace
                />
            }
        />
    </Routes>
)

// WithProviders removed - no longer needed as we use direct tRPC hooks

const WithOrg = () => {
    const { orgListUserData, refreshOrgListUserData } = useOrgListData()
    const orgId = useOrgId()
    const [logoCacheBust, setLogoCacheBust] = useState(Date.now())

    if (!orgListUserData.orgData[orgId!]) {
        return <SizableText size="$6">Org not found</SizableText>
    }

    const orgData = orgListUserData.orgData[orgId!]
    const isMobile = useIsMobile()
    const showOnboarding = orgData.onboarding?.completed !== true && !isMobile

    const content = (
        <AiHelpProvider>
            <View
                flexDirection="column"
                height="100vh"
            >
                <TopBar />
                <View
                    flexDirection="row"
                    flex={1}
                    minHeight={0}
                >
                    <View
                        // @ts-ignore
                        tag="main"
                        className="MainContent"
                        paddingTop="$4"
                        paddingBottom="$4"
                        $md={{ paddingBottom: '$6' }}
                        flex={1}
                        flexDirection="column"
                        minWidth={0}
                        gap="$1"
                        overflow="auto"
                    >
                        <OrgRoutes />
                    </View>
                    {showOnboarding && <OnboardingGuide />}
                </View>
            </View>
            {showOnboarding ? <AiHelpWidgetWithOnboarding /> : <AiHelpWidget />}
            <AiHelpHighlight />
            {showOnboarding && <OnboardingHighlight />}
        </AiHelpProvider>
    )

    return (
        <UserDataContext.Provider
            value={{
                user: {
                    ...omit(['orgData'], orgListUserData),
                    ...orgData,
                    orgLogo: orgData.orgLogo ? `${orgData.orgLogo}?t=${logoCacheBust}` : orgData.orgLogo,
                },
                refreshUser: refreshOrgListUserData,
                logoCacheBust,
                bustLogoCache: () => setLogoCacheBust(Date.now()),
            }}
        >
            <GeneralWebsocketProvider>
                {showOnboarding ? (
                    <OnboardingProvider>
                        {content}
                    </OnboardingProvider>
                ) : (
                    content
                )}
            </GeneralWebsocketProvider>
        </UserDataContext.Provider>
    )
}

const LoggedInRoutes = () => {
    const { orgListUserData } = useOrgListData()
    const orgs = Object.entries(orgListUserData.orgData)

    return (
        <Routes>
            {/* Person-level / cross-org surfaces — static paths rank above /:orgId/* */}
            <Route
                path="/affiliate/*"
                element={<AffiliateApp />}
            />
            <Route
                path="/admin/*"
                element={<AdminApp />}
            />
            <Route
                path="/:orgId/*"
                element={<WithOrg />}
            />
            <Route
                index
                element={
                    orgs.length === 0 ? (
                        <Navigate
                            to="/sign-up"
                            replace
                        />
                    ) : orgs.length === 1 ? (
                        <Navigate to={orgs[0][0]} />
                    ) : (
                        <OrgList />
                    )
                }
            />
        </Routes>
    )
}

const AppRoutes = () => {
    const [searchParams] = useSearchParams()
    const expoPushToken = searchParams.get('expoPushToken')

    useEffect(() => {
        if (expoPushToken) {
            localStorage.setItem('expoPushToken', expoPushToken)
        }
    }, [expoPushToken])

    // Capture an affiliate `?ref=` code as early as possible so it survives the Clerk
    // sign-up and Stripe Checkout full-page redirects (consumed in the signup flow).
    useEffect(() => {
        captureRefFromUrl()
    }, [])

    return (
        <Routes>
            <Route
                path="/xero/auth"
                element={<XeroConnectionResponse />}
            />
            <Route
                path="/docusign/auth"
                element={<DocusignConnectionResponse />}
            />
            <Route
                path="/docusign/sender-return"
                element={<DocusignSenderReturn />}
            />
            <Route
                path="/:orgId/wishlist"
                element={<PublicWishlist />}
            />
            <Route
                path="/sign-up"
                element={<SignupWizard />}
            />
            <Route
                path="/*"
                element={
                    <>
                        <Show when="signed-in">
                            <UserProvider>
                                <LoggedInRoutes />
                            </UserProvider>
                        </Show>
                        <Show when="signed-out">
                            <View
                                flexDirection="row"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="100vh"
                                width="100%"
                            >
                                <SignIn signUpUrl="/sign-up" />
                            </View>
                        </Show>
                    </>
                }
            />
        </Routes>
    )
}

export default AppRoutes
